/**
 * Native bridge for Capacitor (iOS/Android).
 *
 * Activates native behaviors only when running inside a native shell.
 * On web, all functions are safe no-ops.
 */
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

/** Returns true when the app is running inside a native Capacitor shell. */
export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Extract a shared URL from a deep link in the `organizer://capture?url=...` format.
 * Returns null if the link is unrelated or malformed.
 */
export function parseSharedUrlFromDeepLink(link: string): string | null {
  try {
    const u = new URL(link);
    if (u.hostname !== 'capture' && !u.pathname.includes('capture')) return null;
    const shared = u.searchParams.get('url');
    return shared ? decodeURIComponent(shared) : null;
  } catch {
    return null;
  }
}

/**
 * Wire native listeners. Safe to call on web (no-ops).
 *
 * @param onCaptureUrl - callback fired when a URL is received via deep link or share
 */
export async function initNative(onCaptureUrl: (url: string) => void): Promise<void> {
  if (!isNative()) return;

  // Deep links via custom scheme or app links
  App.addListener('appUrlOpen', ({ url }) => {
    const shared = parseSharedUrlFromDeepLink(url);
    if (shared) onCaptureUrl(shared);
  });

  // Share target (Android receives shares as deep-linked events via plugin)
  try {
    // Fully dynamic to prevent Vite from resolving at build time
    const pkgName = '@capawesome-team/capacitor-share-target';
    const mod = await (new Function('p', 'return import(p)'))(pkgName);
    const ShareTarget = mod?.ShareTarget;
    if (ShareTarget?.addListener) {
      await ShareTarget.addListener('shareReceived', (event: { url?: string; text?: string }) => {
        const shared = event.url || event.text;
        if (shared) onCaptureUrl(shared);
      });
    }
  } catch {
    // share-target plugin not installed yet — ignore
  }

  // Push notification registration
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive === 'granted') {
      await PushNotifications.register();

      // Register device token with backend
      PushNotifications.addListener('registration', async (token) => {
        try {
          const { apiClient } = await import('@/api/client');
          await apiClient.request('/notifications/register-device', {
            method: 'POST',
            body: JSON.stringify({ token: token.value, platform: Capacitor.getPlatform() }),
          });
        } catch {
          /* offline; will retry next launch */
        }
      });
    }
  } catch {
    // push plugin not installed / not native — ignore
  }

  // Status bar theming (Nocturne dark)
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#09090c' });
  } catch {
    /* not native or plugin missing */
  }
}

/**
 * Require biometric unlock. Returns true if unlocked or biometrics unavailable.
 * Gates app open behind biometric when a credential is enrolled.
 */
export async function requireBiometricUnlock(): Promise<boolean> {
  if (!isNative()) return true;
  try {
    // Fully dynamic to prevent Vite from resolving at build time
    const pkgName = '@aparajita/capacitor-biometric-auth';
    const mod = await (new Function('p', 'return import(p)'))(pkgName);
    const BiometricAuth = mod?.BiometricAuth;
    if (!BiometricAuth) return true;
    const result = await BiometricAuth.checkBiometry();
    if (!result.isAvailable) return true;
    await BiometricAuth.authenticate({ reason: 'Unlock Organizer' });
    return true;
  } catch {
    // User cancelled or biometric failed — don't unlock
    return false;
  }
}

/**
 * Trigger haptic feedback. No-ops on web.
 */
export async function triggerHaptic(style: 'light' | 'medium' | 'heavy' = 'medium'): Promise<void> {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    const styleMap = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    };
    await Haptics.impact({ style: styleMap[style] });
  } catch {
    /* haptics not available */
  }
}
